/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useDropLocations } from "../../store/useDropLocations";
import { useMarketPricing } from "../../store/useMarketPricing";
import {
  sortLocationNames,
  getOrderedLabelsFromFilterPlaces,
} from "../../utils/locationOrdering";
import {
  getStoreColor,
  getCityColor,
  TOTAL_CONTRAST_COLOR,
} from "../../constants/plasticColors";
import InformationIcon from "../../assets/icons/info/information.png";
import { Tooltip } from "@material-tailwind/react";
import {
  MaterialsChartFilters,
  getDefaultMaterialsChartFilters,
  type MaterialsChartFiltersState,
} from "../../pages/Volumes/MaterialsChartFilters";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";
import { ApexOptions } from "apexcharts";

const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";
const ALL_MATERIALS_ID = "__ALL_MATERIALS__";

interface MaterialVolumesPerStoreProps {
  filterType: "location" | "city";
  filterPlaces: string[];
  sequenceStep?: number;
  setSequenceStep?: (n: number) => void;
  sequenceStepIndex?: number;
}

export const MaterialVolumesPerStore = React.memo(
  ({
    filterType,
    filterPlaces,
    sequenceStep,
    setSequenceStep,
    sequenceStepIndex,
  }: MaterialVolumesPerStoreProps) => {
    const snapshotRef = useRef(null);
    const [_, takeScreenshot] = useScreenshot();
    const { dropLocations } = useDropLocations();
    const { marketPrices, getAllMarketPrices } = useMarketPricing();

    const [filters, setFilters] = useState<MaterialsChartFiltersState>(
      getDefaultMaterialsChartFilters
    );
    const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(
      null
    );
    const [timeSeriesData, setTimeSeriesData] = useState<{
      timeLabels: string[];
      storeSeries: { name: string; data: number[] }[];
    } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isCsvDownloading, setIsCsvDownloading] = useState(false);

    const materialOptions = useMemo(() => {
      if (!marketPrices?.length) return [];
      return [
        { id: ALL_MATERIALS_ID, name: "All materials" },
        ...marketPrices
          .filter((m) => m.measurement === "kg")
          .map((m) => ({ id: m._id!, name: m.name })),
      ];
    }, [marketPrices]);

    const allKgMaterialIds = useMemo(
      () =>
        materialOptions
          .filter((m) => m.id !== ALL_MATERIALS_ID)
          .map((m) => m.id),
      [materialOptions]
    );

    useEffect(() => {
      getAllMarketPrices();
    }, []);

    useEffect(() => {
      if (materialOptions.length > 0 && !selectedMaterialId) {
        const textilesOption =
          materialOptions.find((m) =>
            m.id !== ALL_MATERIALS_ID &&
            m.name.toLowerCase().includes("textiles")
          ) ?? materialOptions.find((m) => m.id !== ALL_MATERIALS_ID);
        if (!textilesOption) return;
        setSelectedMaterialId(textilesOption.id);
      }
    }, [materialOptions, selectedMaterialId]);

    const prevFilterSigRef = useRef<string | null>(null);

    useEffect(() => {
      if (!selectedMaterialId) {
        setTimeSeriesData(null);
        setIsLoading(false);
        if (
          sequenceStep !== undefined &&
          setSequenceStep !== undefined &&
          sequenceStepIndex !== undefined &&
          sequenceStep === sequenceStepIndex
        ) {
          setSequenceStep(sequenceStepIndex + 1);
        }
        return;
      }
      if (
        sequenceStep !== undefined &&
        setSequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep < sequenceStepIndex
      ) {
        return;
      }
      const filterSig = JSON.stringify({
        filters,
        filterType,
        selectedMaterialId,
      });
      if (
        sequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep > sequenceStepIndex &&
        prevFilterSigRef.current === filterSig
      ) {
        return;
      }
      prevFilterSigRef.current = filterSig;

      const {
        period,
        fromYear,
        toYear,
        fromMonth,
        toMonth,
        fromQuarter,
        toQuarter,
        fromWeek,
        toWeek,
      } = filters;
      const body: Record<string, unknown> = {
        type: filterType,
        places: [],
        period,
        fromYear,
        toYear,
      };
      if (selectedMaterialId === ALL_MATERIALS_ID) {
        body.materialIds = allKgMaterialIds;
      } else {
        body.materialId = selectedMaterialId;
      }
      if (period === "month") {
        body.fromMonth = fromMonth;
        body.toMonth = toMonth;
      } else if (period === "quarter") {
        body.fromQuarter = fromQuarter;
        body.toQuarter = toQuarter;
      } else if (period === "week") {
        body.fromWeek = fromWeek;
        body.toWeek = toWeek;
      }

      setIsLoading(true);
      setHasError(false);
      axios
        .post("locations/stats/materials-per-store-over-time", body)
        .then((res) => {
          if (res?.data) setTimeSeriesData(res.data);
        })
        .catch(() => setHasError(true))
        .finally(() => {
          setIsLoading(false);
          if (
            setSequenceStep != null &&
            sequenceStepIndex != null &&
            sequenceStep === sequenceStepIndex
          ) {
            setSequenceStep(sequenceStepIndex + 1);
          }
        });
    }, [
      filters,
      filterType,
      selectedMaterialId,
      sequenceStep,
      setSequenceStep,
      sequenceStepIndex,
      allKgMaterialIds,
    ]);

    const { chartSeries, locationColors } = useMemo(() => {
      if (isLoading && !timeSeriesData) {
        return {
          chartSeries: [],
          locationColors: [],
        };
      }
      if (!timeSeriesData?.storeSeries?.length) {
        return {
          chartSeries: [],
          locationColors: [],
        };
      }

      const rawSeries = timeSeriesData.storeSeries;
      const apiTotalSeries = rawSeries.find(
        (s) => s.name === "Total all stores"
      );
      const baseSeries = rawSeries.filter((s) => s.name !== "Total all stores");

      const apiLabels = baseSeries.map((s) => s.name);
      const realFilterPlaces = filterPlaces.filter(
        (p) => p !== TOTAL_ALL_STORES_ID
      );

      let orderedLabels: string[];
      if (realFilterPlaces.length > 0) {
        const userOrderedLabels = getOrderedLabelsFromFilterPlaces(
          filterPlaces,
          filterType,
          dropLocations,
          TOTAL_ALL_STORES_ID
        );
        const apiLabelsSet = new Set(apiLabels);
        const filtered = userOrderedLabels.filter((label) =>
          apiLabelsSet.has(label)
        );
        // If labels don't match API keys (e.g. formatting farkı) fallback to API order
        orderedLabels =
          filtered.length > 0
            ? filtered
            : sortLocationNames(apiLabels, dropLocations);
      } else {
        orderedLabels = sortLocationNames(apiLabels, dropLocations);
      }

      const series = orderedLabels.map((label) => {
        const s = baseSeries.find((x) => x.name === label);
        return s ? { name: s.name, data: s.data } : { name: label, data: [] };
      });

      const includeTotalAllStores = filterPlaces.includes(TOTAL_ALL_STORES_ID);

      let finalSeries = series;
      if (includeTotalAllStores) {
        if (apiTotalSeries) {
          finalSeries = [...series, apiTotalSeries];
        } else {
          const pointsCount = timeSeriesData.timeLabels.length;
          const totalData = Array(pointsCount).fill(0);
          series.forEach((s) => {
            for (let i = 0; i < pointsCount; i++) {
              const val = Number(s.data[i] ?? 0);
              totalData[i] += Number.isFinite(val) ? val : 0;
            }
          });
          finalSeries = [
            ...series,
            { name: "Total all stores", data: totalData },
          ];
        }
      }

      const getLocationColor = (label: string) => {
        if (label === "Total all stores") return TOTAL_CONTRAST_COLOR;
        if (filterType === "city") return getCityColor(label);
        const short = (label.split(" ")[0] ?? label).trim();
        return getStoreColor(short);
      };

      return {
        chartSeries: finalSeries,
        locationColors: finalSeries.map((s) => getLocationColor(s.name)),
      };
    }, [timeSeriesData, filterType, filterPlaces, dropLocations]);

    const hasData = chartSeries.some((s) => s.data.some((v: number) => v > 0));
    const showLoading = isLoading;

    const chartOptions = {
      chart: {
        type: "line",
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        strokeDashArray: 5,
      },
      markers: {
        size: 1,
      },
      legend: {
        show: true,
        position: "right",
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
          // Create an array of series with their values
          const seriesData = series.map((seriesValues: any, index: number) => ({
            name: w.globals.seriesNames[index],
            value: seriesValues[dataPointIndex],
            color: w.config.colors[index],
          }));

          // Sort by value in descending order (highest to lowest)
          seriesData.sort((a: any, b: any) => b.value - a.value);

          // Build the tooltip HTML with original ApexCharts styling
          let tooltipHtml =
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';

          // Use category label (timeLabels) for the x-axis value
          const label =
            w.globals?.labels?.[seriesIndex]?.[dataPointIndex] ??
            w.globals?.labels?.[0]?.[dataPointIndex] ??
            "";
          tooltipHtml += `${label}</div>`;

          seriesData.forEach((item: any) => {
            const formattedValue = new Intl.NumberFormat("nl-NL", {
              style: "decimal",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(item.value);

            tooltipHtml += `
              <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${
                  item.color
                };"></span>
                <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                  <div class="apexcharts-tooltip-y-group">
                    <span class="apexcharts-tooltip-text-y-label">${
                      item.name
                    }: </span>
                    <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
                  </div>
                </div>
              </div>
            `;
          });

          return tooltipHtml;
        },
      },
      yaxis: {
        title: {
          text: "Volume (kg)",
          style: {
            fontSize: "14px",
            fontWeight: "bold",
          },
        },
        labels: {
          formatter: function (value: number) {
            return new Intl.NumberFormat("nl-NL", {
              style: "decimal",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          },
        },
      },
      colors:
        locationColors.length > 0
          ? locationColors
          : [
              "#e34e76",
              "#007887",
              "#8773aa",
              "#e6e1ff",
              "#fe3e3e",
              "#ffff78",
              "#d2e6ff",
              "#d2d2b2",
              "#5fd0ae",
              "#ff8dfa",
              "#ff6e41",
              "#808080",
            ],
      xaxis: {
        categories: timeSeriesData?.timeLabels ?? [],
        labels: {
          style: {
            fontSize: "12px",
          },
        },
      },
    };

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "material_volumes_per_store.png";
        link.click();
      });
    };

    const downloadCsv = async () => {
      if (!selectedMaterialId) return;
      setIsCsvDownloading(true);
      try {
        const selectedMaterialName =
          materialOptions.find((m) => m.id === selectedMaterialId)?.name ??
          "Material";
        const realPlaces =
          (filterPlaces ?? []).filter((place) => place !== TOTAL_ALL_STORES_ID) ??
          [];
        const body: Record<string, unknown> = {
          type: filterType,
          places: realPlaces,
          period: filters.period,
          granularity: "day",
          fromYear: filters.fromYear,
          toYear: filters.toYear,
        };
        if (selectedMaterialId === ALL_MATERIALS_ID) {
          body.materialIds = allKgMaterialIds;
        } else {
          body.materialId = selectedMaterialId;
        }
        if (filters.period === "month") {
          body.fromMonth = filters.fromMonth;
          body.toMonth = filters.toMonth;
        } else if (filters.period === "quarter") {
          body.fromQuarter = filters.fromQuarter;
          body.toQuarter = filters.toQuarter;
        } else if (filters.period === "week") {
          body.fromWeek = filters.fromWeek;
          body.toWeek = filters.toWeek;
        }

        const response = await axios.post(
          "locations/stats/materials-per-store-over-time",
          body
        );
        const timeLabels = response.data?.timeLabels ?? [];
        const storeSeries = response.data?.storeSeries ?? [];
        let csvContent =
          "Date," + storeSeries.map((s: { name: string }) => s.name).join(",") + "\n";
        timeLabels.forEach((label: string, idx: number) => {
          const row = [
            label,
            ...storeSeries.map((s: { data: number[] }) => s.data[idx] ?? 0),
          ];
          csvContent += row.join(",") + "\n";
        });

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `material_volumes_${selectedMaterialName.replace(/\s+/g, "_")}_daily.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading CSV:", error);
      } finally {
        setIsCsvDownloading(false);
      }
    };

    const SpinnerIcon = ({ className = "w-8 h-8" }) => (
      <svg
        aria-hidden="true"
        className={`${className} text-gray-200 animate-spin dark:text-gray-600 fill-primary-base`}
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    );

    const selectedMaterialName =
      materialOptions.find((m) => m.id === selectedMaterialId)?.name ?? "";

    return (
      <div className="flex rounded-xl shadow-none flex-col w-full p-2 sm:p-7 bg-white mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between gap-4">
          <div></div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-start sm:justify-end gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              <span className="font-satoshi text-sm font-medium text-gray-700 whitespace-nowrap">
                Material:
              </span>
              <Menu>
                <MenuHandler>
                  <button className="h-9 px-3 sm:px-4 border-2 rounded-md border-gray-300 bg-gray-50 hover:bg-gray-100 flex items-center justify-center font-satoshi font-semibold text-gray-800 text-sm whitespace-nowrap min-w-[160px] shadow-sm">
                    {selectedMaterialName || "Select material"}
                    <ChevronDown className="ml-1 sm:ml-2 w-4 h-4 flex-shrink-0" />
                  </button>
                </MenuHandler>
                <MenuList
                  className="rounded-none max-h-[300px] overflow-y-auto"
                  placeholder={undefined}
                >
                  {materialOptions.map((m) => (
                    <MenuItem
                      key={m.id}
                      disabled={selectedMaterialId === m.id}
                      onClick={() => setSelectedMaterialId(m.id)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {m.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </div>
            <MaterialsChartFilters
              state={filters}
              onChange={setFilters}
              onDownloadImage={downloadScreenshot}
              onDownloadCsv={downloadCsv}
              isCsvDownloading={isCsvDownloading}
              hasData={hasData}
            />
          </div>
        </div>

        <section ref={snapshotRef} className="mt-4 ">
          <div className="flex items-center mb-2 sm:mb-0">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Volumes per material over time
            </p>
            <Tooltip
              content="Compare stores for a selected material over time. Select one material and view volume trends per store."
              placeholder={undefined}
            >
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
          </div>
          <div className="relative min-h-[320px] mt-4">
            {showLoading ? (
              <div className="flex items-center justify-center h-[300px] w-full">
                <SpinnerIcon className="w-8 h-8" />
              </div>
            ) : hasError ? (
              <div className="flex items-center justify-center h-[300px] w-full">
                <p className="text-red-500 font-satoshi">
                  Failed to load data. Please try again.
                </p>
              </div>
            ) : hasData && chartSeries.length > 0 ? (
              <Chart
                options={chartOptions as ApexOptions}
                series={chartSeries}
                type="line"
                height={300}
              />
            ) : (
              <div className="flex items-center justify-center h-[300px] w-full">
                <p className="text-gray-500 font-satoshi">
                  No data available for the selected material and filters.
                  Select stores above.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }
);
